<template>
  <div class="the-header-root">
    <VBtn
      type="button"
      color="grey lighten-2"
      depressed
      :ripple="false"
      class="the-header__skip-to-content-btn box-shadow-elevation-4 border-radius-8"
      data-test="skip-to-content"
      @click="skipToMainContent"
    >
      <span class="the-header__skip-to-content-btn__label">
        {{ $t('accessibility.skipToContent') }}
      </span>
    </VBtn>
    <SCancellingBookingDialog
      :isCancelingBookingDialogOpen="isCancelingBookingDialogOpen"
      @closeDialog="isCancelingBookingDialogOpen = !isCancelingBookingDialogOpen"
    />
    <STheHeader
      v-if="!isCheckoutPage"
      :isHeaderShown="isHeaderShown"
      :currentLang="getCurrentLang"
      :supportedLanguages="languages"
      :toolbarItems="getStrappiCoreMenu(toolbarItems)"
      :showOnClick="getStrappiCoreSettings.subMenuOnClick"
      :authToken="getAuthToken"
      :firstLetters="getFirstLetters"
      :fullName="getFullName"
      :cabinetToolbarItems="cabinetToolbarItems"
      :isLoadingLogOut="isLoadingLogOut"
      :logo="getStrappiCoreSettings.logo"
      :logoRedirectPath="getLogoRedirectPath"
      @setLang="setLang"
      @toAuthPage="toAuthPage"
      @logOut="logOut"
    />
    <SCheckoutHeader
      v-else
      :isHeaderShown="isHeaderShown"
      :appConfig="getAppConfig"
      :adultsCount="getAdults"
      :infantsCount="getChildren"
      :animalsTransportation="getAnimalsTransportation"
      :vehicleCategories="getVehicleCategories"
      :vehicleCategoryId="getVehicleCategoryId"
      :tripType="getTripType"
      :rideType="getRideType"
      :paymentType="getPaymentType"
      :selectedFeatures="getSelectedFeatures"
      :temporaryReservationDeadline="getTemporaryReservationDeadline"
      :remainingTime="getTemporaryReservationRemainingTime"
      :isAuth="!!getAuthToken"
      :ignoreRemainingTime="getIgnoreRemainingTime"
      @updateRemainingTime="updateRemainingTime"
      @cancelBooking="isCancelingBookingDialogOpen = !isCancelingBookingDialogOpen"
      @trackGtmEventReservationExpired="onTrackEventReservationExpired"
    />
  </div>
</template>
<script lang="ts">
export default {
  name: 'TheHeader',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  STheHeader, SCancellingBookingDialog, SStrapiService, SCheckoutHeader,
} from '@slg/web-customer-shared';
import appConfig from '@/appConfig';
import { skipToMainContent } from '@/utils/skipToMainContent';
import {
  useStore, useRouter, useRouteRef, useCmsLocaleReload, useGlobalErrorDialog, useGtm,
} from '@/composables';

defineProps({
  isHeaderShown: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();
const router = useRouter();
const route = useRouteRef();
const { setGlobalError } = useGlobalErrorDialog();
const { trackGtmEvent } = useGtm();

const isLoadingLogOut = ref(false);
const isCancelingBookingDialogOpen = ref(false);
const languages = ref<string[] | null>(null);
const toolbarItems = ref([
  {
    strapiText: '',
    strapiKey: 'homeTitle',
    text: 'header.home',
    value: 'Booking',
  },
  {
    text: 'header.about',
    value: 'About',
  },
  {
    text: 'header.nightCard',
    value: 'NightCard',
  },
  {
    strapiText: '',
    strapiKey: 'FAQTitle',
    text: 'header.faq',
    value: 'FAQ',
  },
  {
    strapiText: '',
    strapiKey: 'contactUsTitle',
    text: 'header.contactUs',
    value: 'ContactUs',
  },
]);
const cabinetToolbarItems = ref([
  {
    text: 'cabinet.myTrips',
    icon: 'mdi-ticket-confirmation',
    value: 'Trips',
  },
  {
    text: 'cabinet.settings',
    icon: 'mdi-cog',
    value: 'Profile',
  },
  {
    text: 'cabinet.logOut',
    icon: 'mdi-logout-variant',
    value: 'LogOut',
  },
]);

languages.value = appConfig.languages;

const getAuthToken = computed(() => store.getters['auth/getAuthToken']);
const getFirstLetters = computed(() => store.getters['auth/getFirstLetters']);
const getFullName = computed(() => store.getters['auth/getFullName']);
const getStrappiCoreSettings = computed(() => store.getters['common/getStrappiCoreSettings']);
const getStrappiCoreMenu = computed(() => store.getters['common/getStrappiCoreMenu']);
const getAdults = computed(() => store.getters['booking/getAdults']);
const getChildren = computed(() => store.getters['booking/getChildren']);
const getTripType = computed(() => store.getters['booking/getTripType']);
const getRideType = computed(() => store.getters['booking/getRideType']);
const getTemporaryReservationRemainingTime = computed(() => store.getters['booking/getTemporaryReservationRemainingTime']);
const getIgnoreRemainingTime = computed(() => store.getters['booking/getIgnoreRemainingTime']);
const getAnimalsTransportation = computed(() => store.getters['booking/getAnimalsTransportation']);
const getVehicleCategoryId = computed(() => store.getters['booking/getVehicleCategoryId']);
const getVehicleCategories = computed(() => store.getters['opsZone/getVehicleCategories']);
const getSelectedFeatures = computed(() => store.getters['booking/getSelectedFeatures']);
const getTemporaryReservationDeadline = computed(() => store.getters['booking/getTemporaryReservationDeadline']);
const getPaymentType = computed(() => store.getters['booking/getPaymentType']);
const getAppConfig = computed(() => appConfig);
const getCurrentLang = computed(() => route.value.params.lang);
const getLogoRedirectPath = computed(() => {
  const { defaultLanguage } = appConfig;
  const currentLang = route.value.params.lang || defaultLanguage;
  return { name: 'Booking', params: { lang: currentLang } };
});
// due to problems with specific header and cookie banner on Chekout Page
const isCheckoutPage = computed(() => route.value.name === 'Checkout');

function onTrackEventReservationExpired() {
  trackGtmEvent('Booking-Expired-Dialog-Ok-Button-Click', 'Button', 'Click', 'Redirect to home page');
}

async function loadCmsData() {
  try {
    const res = await SStrapiService.getCoreSettings();
    store.commit('common/setStrappiCoreSettings', res);
  } catch (error) {
    // to-do rethink about catch
  }
}

function setLang(lang: string) {
  if (lang === getCurrentLang.value) {
    return;
  }
  router.push({
    name: route.value.name,
    params: {
      ...route.value.params,
      lang,
    },
    query: route.value.query,
  }).catch(() => {});
}

function toAuthPage() {
  router.push({ name: 'Auth' });
}

async function logOut() {
  isLoadingLogOut.value = true;
  try {
    store.commit('booking/clearTemporaryReservation');
  } catch (e) {
    await setGlobalError(e as any);
  }
  isLoadingLogOut.value = false;
  store.commit('auth/clearAuthTokenAndProfileInfo');
  if (route.value.name !== 'Booking') {
    await router.push({ name: 'Booking' });
  }
}

function updateRemainingTime(remainingTime: number) {
  store.commit('booking/setRemainingTime', remainingTime);
}

useCmsLocaleReload(loadCmsData);
</script>

<style scoped lang="scss">
@import "@/style/headerLinks.scss";
.header {
  transform: translateY(0);
  transition: transform 300ms linear !important;
}
.header.is-hidden {
    transform: translateY(-100%) !important;
}

.feature-logo {
  width: 24px;
  height: 24px;
  opacity: 0;

  &--show {
    opacity: 1;
  }
}

.ps {
  width: auto;
  bottom: 0;
}

</style>

<style lang="scss">
@import "@/style/variables.scss";

.the-header-root {
  position: relative;
}

.the-header__skip-to-content-btn.v-btn {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 1px !important;
  min-width: 1px !important;
  max-width: 1px !important;
  height: 1px !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0 !important;
  z-index: 10001;
  box-shadow: none !important;
  font-family: $ff-roboto, sans-serif;
  font-size: $fs-14 !important;
  font-weight: $fw-500 !important;
  letter-spacing: $ls-01 !important;
  line-height: $lh-20 !important;
  text-transform: none !important;
  border-radius: 0 !important;

  .the-header__skip-to-content-btn__label {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:focus {
    position: fixed;
    left: max(1.2rem, calc((100vw - 1280px) / 2));
    top: 75px;
    width: auto !important;
    min-width: 12rem !important;
    max-width: min(22rem, calc(100vw - 2.4rem)) !important;
    height: auto !important;
    min-height: 4rem !important;
    padding: 0.8rem 1.6rem !important;
    overflow: visible;
    clip: auto;
    border-radius: $br-8 !important;
    box-shadow: 0 2px 4px 0 #0000001a !important;
    outline: none;
    background-color: #eeeeee !important;
    color: var(--v-textPrimary-base, #252525) !important;
    transform: none;
  }

  &:focus-visible {
    box-shadow:
      0 2px 4px 0 #0000001a,
      0 0 0 3px var(--v-secondary-base, #0075a9) !important;
  }
}
</style>
<style src="vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css"/>
